import type {Filter} from "crqlar-react-common/dist/models/audience/filter.model";
import {normalizeFilter} from "../../utils/filterUrlState";

const AUDIENCE_FILTER_VERSION = 1;
const AUDIENCE_MEMBER_LIST_PATH = '/marketing/audience/members';

const PARAMS = {
    audienceId: 'audienceId',
    filter: 'audienceFilter',
    mode: 'mode',
    legacyAudienceId: 'id',
    legacyFilter: 'filter'
} as const;

export type AudienceMemberMode = 'audience' | 'marketing';

export interface AudienceMemberUrlState {
    audienceId?: string
    filter?: Filter
    mode: AudienceMemberMode
}

export interface ParsedAudienceMemberUrlState extends AudienceMemberUrlState {
    invalidFilter: boolean
    needsMigration: boolean
}

interface ParsedFilter {
    filter?: Filter
    invalid: boolean
}

const parseModernFilter = (raw: string): ParsedFilter => {
    try {
        const payload = JSON.parse(raw) as {version?: unknown; filter?: unknown};
        if (payload?.version !== AUDIENCE_FILTER_VERSION) return {invalid: true};

        const filter = normalizeFilter(payload.filter);
        return filter ? {filter, invalid: false} : {invalid: true};
    } catch {
        return {invalid: true};
    }
};

const parseLegacyFilter = (raw: string): ParsedFilter => {
    try {
        if (typeof atob !== 'function') return {invalid: true};
        const filter = normalizeFilter(JSON.parse(atob(raw.replace(/ /g, '+'))));
        return filter ? {filter, invalid: false} : {invalid: true};
    } catch {
        return {invalid: true};
    }
};

const nonEmpty = (value: string | null) => value?.trim() || undefined;

export const parseAudienceMemberUrlState = (
    searchParams: URLSearchParams
): ParsedAudienceMemberUrlState => {
    const modernAudienceId = nonEmpty(searchParams.get(PARAMS.audienceId));
    const legacyAudienceId = modernAudienceId
        ? undefined
        : nonEmpty(searchParams.get(PARAMS.legacyAudienceId));
    const audienceId = modernAudienceId ?? legacyAudienceId;
    const mode: AudienceMemberMode = searchParams.get(PARAMS.mode) === 'marketing'
        ? 'marketing'
        : 'audience';

    if (audienceId) {
        return {
            audienceId,
            mode,
            invalidFilter: false,
            needsMigration: !!legacyAudienceId
        };
    }

    const modernFilter = searchParams.get(PARAMS.filter);
    if (modernFilter !== null) {
        const parsed = parseModernFilter(modernFilter);
        return {...parsed, mode, invalidFilter: parsed.invalid, needsMigration: false};
    }

    const legacyFilter = searchParams.get(PARAMS.legacyFilter);
    if (legacyFilter !== null) {
        const parsed = parseLegacyFilter(legacyFilter);
        return {
            ...parsed,
            mode,
            invalidFilter: parsed.invalid,
            needsMigration: !parsed.invalid
        };
    }

    return {mode, invalidFilter: false, needsMigration: false};
};

export const writeAudienceMemberUrlState = (
    searchParams: URLSearchParams,
    {audienceId, filter, mode}: AudienceMemberUrlState
) => {
    Object.values(PARAMS).forEach(param => searchParams.delete(param));

    if (audienceId) {
        searchParams.set(PARAMS.audienceId, audienceId);
    } else if (filter) {
        const normalizedFilter = normalizeFilter(filter);
        if (normalizedFilter) {
            searchParams.set(PARAMS.filter, JSON.stringify({
                version: AUDIENCE_FILTER_VERSION,
                filter: normalizedFilter
            }));
        }
    }

    if (mode === 'marketing') searchParams.set(PARAMS.mode, mode);
};

export const createAudienceMemberListUrl = (state: AudienceMemberUrlState) => {
    const searchParams = new URLSearchParams();
    writeAudienceMemberUrlState(searchParams, state);
    const query = searchParams.toString();
    return query ? `${AUDIENCE_MEMBER_LIST_PATH}?${query}` : AUDIENCE_MEMBER_LIST_PATH;
};
